/// <amd-module name="Release/Commands/ShowSecurityCommandService" />
import * as React from "react";
import * as ReactDOM from "react-dom";

import { Spinner, SpinnerSize } from "VSSUI/Spinner";

import { IVssService, VssService, Services } from "VSS/Platform/Context";
import { ReactRootComponent } from "VSS/Platform/Layout";
import { ILegacyComponentProperties, LegacyComponent } from "VSS/Legacy/Legacy";

import { IMenuItem } from "VSSUI/Menu";

import { getTFSPageData } from "Tfs/Platform/Page";

import { IReleaseDefinitionPivotCommandContext } from "Release/Client/ExtensionContracts/IMenuCommandContracts";
import { ReleaseManagementNamespace, createFolderPathSecurityToken, userHasMemberClaim } from "Release/Helpers/Permission";
import { publishClickEvent } from "Release/Helpers/Telemetry";
import * as Resources from "Release/Commands/Resources";

export const pipelineSecurityCommand: (context: IReleaseDefinitionPivotCommandContext) => IMenuItem | undefined = (context: IReleaseDefinitionPivotCommandContext) => {
    // Show security dialog for all member users (non anonymous and non public)
    // This command is only for showing the permissions that the member user has, not for allowing editing, hence AdministerReleasePermissions check is not required here
    // Security dialog does its own handling of permissions
    if (userHasMemberClaim(context.pageContext)) {
        return {
            id: "release-pipeline-security-command",
            text: Resources.Security,
            iconProps: { iconName: "Shield" },
            onActivate: () => {
                context.pageContext.getService<IShowSecurityCommandService>("IShowSecurityCommandService").showPipelineSecurity(context);
            }
        };
    }
};

export interface IShowSecurityCommandService extends IVssService {
    showPipelineSecurity(context: IReleaseDefinitionPivotCommandContext): void;
}

interface ISecurityDialogParameters {
    resourceName: string;
    token: string;
    projectId: string;
    permissionSet: string;
}

class ShowSecurityCommandService extends VssService implements IShowSecurityCommandService {
    public showPipelineSecurity(context: IReleaseDefinitionPivotCommandContext): void {
        if (context.pipelineId) {
            publishClickEvent(this.pageContext, setDefinitionLevelSecurityFeature, context.telemetrySource);
        }
        else {
            publishClickEvent(this.pageContext, setFolderLevelSecurityFeature, context.telemetrySource);
        }

        const pageData = getTFSPageData(this.pageContext);
        const projectId = (pageData && pageData.project && pageData.project.id) || "";

        let resourceName = Resources.AllPipelines;
        if (context.pipelineName) {
            resourceName = context.pipelineName;
        }
        else if (context.folderPath !== pathSeparator) {
            resourceName = context.folderPath;
        }

        const parameters: ISecurityDialogParameters = {
            resourceName: resourceName,
            token: createFolderPathSecurityToken(context.folderPath, context.pipelineId),
            projectId: projectId,
            permissionSet: ReleaseManagementNamespace,
        };

        this._renderSecurityDialog(parameters);
    }

    private _renderSecurityDialog(parameters: any) {
        ReactDOM.unmountComponentAtNode(securityDialogHost);

        ReactDOM.render(
            React.createElement(ReactRootComponent,
                {
                    pageContext: this.pageContext
                },
                React.createElement(LegacyComponent, {
                    modules: ["PipelineWorkflow/Scripts/Shared/LegacyLWP/SecurityDialogProxy"],
                    dependentContributionIds: ["ms.vss-releaseManagement-web.release-service-data"],
                    wrappedType: "security-dialog-proxy",
                    wrappedProps: { parameters: parameters },
                    loadingComponent: () => React.createElement(Spinner, { className: "flex flex-grow absolute-fill", size: SpinnerSize.large })
                } as ILegacyComponentProperties)),
            securityDialogHost);
    }
}

const pathSeparator = "\\";
const securityDialogHost = document.createElement("div");
const setDefinitionLevelSecurityFeature: string = "Command_DefinitionLevelSecurity";
const setFolderLevelSecurityFeature: string = "Command_FolderLevelSecurity";

Services.add("IShowSecurityCommandService", { serviceFactory: ShowSecurityCommandService });
